/**
 * AccountForm
 *
 * Modal form for creating and editing accounts.
 * Includes bank-detail fields shown conditionally for cash/bank subtypes.
 */

import { useEffect, useRef, useMemo } from 'react'

import { Button, Modal, Input, Select, FormField, Checkbox, Badge } from '@elsapiens/ui'
import { AlertTriangle } from 'lucide-react'

import { BankDetailsSection } from './AccountForm-bank'
import { ReservedBookSection } from './AccountForm-reserved'
import { SiblingsPopup } from './AccountFormWizard-parts'
import { getSaveButtonLabel, normalizeTag, type AccountFormData } from './chart-of-accounts-helpers'
import { CurrencyConfirmDialog } from '../../components/ui'
import { ACCOUNT_TYPE_LABELS, isBankOnlySubtype, ACCOUNT_SUBTYPE_OPTIONS, EQUITY_SUBTYPE_OPTIONS } from '../../constants/labels'
import { validateAccountCode, checkReservedCode, TYPE_PREFIXES } from '../../constants/reserved-account-codes'

import type { Account, AccountType } from '../../services/account-service'


const ACCOUNT_TYPE_OPTIONS = Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({ value, label }))

const ENTITY_MANAGED_SUBTYPES = new Set(['Accounts Receivable', 'Accounts Payable', 'Customer Advances', 'Vendor Advances'])

interface AccountFormProps {
  isOpen: boolean
  editingAccount: Account | null
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  isSaving: boolean
  isLoadingBankDetails: boolean
  tagInput: string
  setTagInput: (v: string) => void
  parentOptions: Array<{ value: string; label: string }>
  currencies: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  isShowCurrencyConfirm: boolean
  onClose: () => void
  onSave: () => void
  onCurrencyConfirmClose: () => void
  onCurrencyConfirm: () => void
  baseCurrency: string
}

interface AccountFormBodyProps {
  editingAccount: Account | null
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  parentOptions: Array<{ value: string; label: string }>
  accountsMap: Map<string, Account>
  codeError: string | undefined
  codeWarning: string | undefined
  tagInput: string
  setTagInput: (v: string) => void
  currencies: Array<{ value: string; label: string }>
  isEditing: boolean
  isLoadingBankDetails: boolean
  isUnderCashOrBank: boolean
}

function DescriptionField({ formData, setFormData }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>> }): JSX.Element {
  return (
    <FormField label="Description">
      <Input
        placeholder="Optional description"
        value={formData.description}
        onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
      />
    </FormField>
  )
}

function SystemTagField({ tag }: { tag: string }): JSX.Element {
  return (
    <FormField helperText="Managed by the system. Used by built-in features." label="System Tag">
      <div className="flex items-center h-9 px-3">
        <Badge size="sm" variant="info">{tag.replace(/_/g, ' ')}</Badge>
      </div>
    </FormField>
  )
}

function CurrencyField({ formData, setFormData, currencies, isEditing }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>; currencies: Array<{ value: string; label: string }>; isEditing: boolean }): JSX.Element {
  return (
    <FormField required label="Currency">
      <Select
        disabled={isEditing}
        options={currencies}
        value={formData.currency}
        onChange={value => setFormData(prev => ({ ...prev, currency: value }))}
      />
    </FormField>
  )
}

function HeaderCheckbox({ formData, setFormData }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>> }): JSX.Element {
  return (
    <Checkbox
      hideMessage
      checked={formData.isHeader}
      label="This is a header account (can have child accounts)"
      onChange={(checked) => setFormData(prev => ({ ...prev, isHeader: checked, accountSubtype: checked ? '' : prev.accountSubtype }))}
    />
  )
}

function SubtypeField({ formData, setFormData, isEditing }: { formData: AccountFormData; setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>; isEditing: boolean }): JSX.Element {
  const options = formData.accountType === 'ACCOUNT_TYPE_EQUITY'
    ? EQUITY_SUBTYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))
    : ACCOUNT_SUBTYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))
  return (
    <FormField label="Account Subtype">
      <Select
        disabled={isEditing}
        options={options}
        value={formData.accountSubtype}
        onChange={value => setFormData(prev => ({ ...prev, accountSubtype: value }))}
      />
    </FormField>
  )
}

function AccountFormBody(p: AccountFormBodyProps): JSX.Element {
  const isShowSubtype = !p.formData.isHeader && (
    (p.formData.accountType === 'ACCOUNT_TYPE_ASSET' && p.isUnderCashOrBank) ||
    p.formData.accountType === 'ACCOUNT_TYPE_EQUITY'
  )
  return (
    <div className="flex flex-col el-gap-field">
      <TypeAndParentRow
        editingAccount={p.editingAccount}
        formData={p.formData}
        parentOptions={p.parentOptions}
        setFormData={p.setFormData}
      />
      <CodeAndNameRow
        accountsMap={p.accountsMap}
        codeError={p.codeError}
        codeWarning={p.codeWarning}
        editingAccount={p.editingAccount}
        formData={p.formData}
        setFormData={p.setFormData}
      />
      <DescriptionField formData={p.formData} setFormData={p.setFormData} />
      {p.editingAccount?.systemTag && <SystemTagField tag={p.editingAccount.systemTag} />}
      <TagsField
        formData={p.formData}
        setFormData={p.setFormData}
        setTagInput={p.setTagInput}
        tagInput={p.tagInput}
      />
      <CurrencyField currencies={p.currencies} formData={p.formData} isEditing={p.isEditing} setFormData={p.setFormData} />
      {!p.isEditing && <HeaderCheckbox formData={p.formData} setFormData={p.setFormData} />}
      {isShowSubtype && (
        <SubtypeField formData={p.formData} isEditing={p.isEditing} setFormData={p.setFormData} />
      )}
      {isBankOnlySubtype(p.formData.accountSubtype) && (
        <BankDetailsSection
          formData={p.formData}
          isLoadingBankDetails={p.isLoadingBankDetails}
          setFormData={p.setFormData}
        />
      )}
      {!p.isEditing && !p.formData.isHeader && (
        <ReservedBookSection formData={p.formData} setFormData={p.setFormData} />
      )}
    </div>
  )
}

function computeIsSaveDisabled(args: {
  isSaving: boolean
  formData: AccountFormData
  isEditing: boolean
  isEntityControlParent: boolean
}): boolean {
  const { isSaving, formData, isEditing, isEntityControlParent } = args
  if (isSaving || !formData.code || !formData.name) {return true}
  if (!isEditing && !!validateAccountCode(formData.code, formData.accountType)) {return true}
  if (!isEditing && isEntityControlParent) {return true}
  return false
}

interface DerivedAccountFormState {
  isEditing: boolean
  saveLabel: string
  codeError: string | undefined
  codeWarning: string | undefined
  isUnderCashOrBank: boolean
  isSaveDisabled: boolean
}

function deriveAccountFormState(
  formData: AccountFormData,
  editingAccount: Account | null,
  isSaving: boolean,
  accountsMap: Map<string, Account>,
): DerivedAccountFormState {
  const isEditing = !!editingAccount
  // Format/type errors block save; reserved-code mismatches are soft warnings only.
  const codeFormatError = formData.code ? validateAccountCode(formData.code, formData.accountType) : undefined
  const codeWarning = (!codeFormatError && formData.code)
    ? checkReservedCode(formData.code, formData.accountSubtype)
    : undefined
  const selectedParent = formData.parentId ? accountsMap.get(formData.parentId) : undefined
  const isEntityControlParent = !!(
    selectedParent?.isControl &&
    ENTITY_MANAGED_SUBTYPES.has(selectedParent.accountSubtype ?? '')
  )
  const parentNameLower = selectedParent?.name?.toLowerCase() ?? ''
  return {
    isEditing,
    saveLabel: getSaveButtonLabel(isSaving, isEditing),
    codeError: codeFormatError,
    codeWarning,
    isUnderCashOrBank: parentNameLower.includes('cash') || parentNameLower.includes('bank'),
    isSaveDisabled: computeIsSaveDisabled({ isSaving, formData, isEditing, isEntityControlParent }),
  }
}

export function AccountForm(props: AccountFormProps): JSX.Element {
  const {
    isOpen, editingAccount, formData, setFormData, isSaving, isLoadingBankDetails,
    tagInput, setTagInput, parentOptions, currencies, accountsMap, isShowCurrencyConfirm,
    onClose, onSave, onCurrencyConfirmClose, onCurrencyConfirm, baseCurrency,
  } = props
  const derived = deriveAccountFormState(formData, editingAccount, isSaving, accountsMap)

  return (
    <>
      <Modal
        isOpen={isOpen}
        size={isBankOnlySubtype(formData.accountSubtype) ? 'lg' : 'md'}
        title={derived.isEditing ? 'Edit Account' : 'Create Account'}
        onClose={onClose}
      >
        <AccountFormBody
          accountsMap={accountsMap}
          codeError={derived.codeError}
          codeWarning={derived.codeWarning}
          currencies={currencies}
          editingAccount={editingAccount}
          formData={formData}
          isEditing={derived.isEditing}
          isLoadingBankDetails={isLoadingBankDetails}
          isUnderCashOrBank={derived.isUnderCashOrBank}
          parentOptions={parentOptions}
          setFormData={setFormData}
          setTagInput={setTagInput}
          tagInput={tagInput}
        />

        <div className="flex justify-end el-gap-control-md el-mt-lg">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={derived.isSaveDisabled} onClick={onSave}>{derived.saveLabel}</Button>
        </div>
      </Modal>

      <CurrencyConfirmDialog
        currencyCode={formData.currency !== '' ? formData.currency : baseCurrency}
        entityLabel="Account"
        isLoading={isSaving}
        isOpen={isShowCurrencyConfirm}
        onClose={onCurrencyConfirmClose}
        onConfirm={onCurrencyConfirm}
      />
    </>
  )
}

// ── Sub-sections ──────────────────────────────────────────────────────────────

interface RowProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  editingAccount: Account | null
}

function TypeAndParentRow({ formData, setFormData, editingAccount, parentOptions }: RowProps & { parentOptions: Array<{ value: string; label: string }> }): JSX.Element {
  return (
    <div className="grid grid-cols-2 el-gap-field">
      <FormField required label="Account Type">
        <Select
          disabled={!!editingAccount}
          options={ACCOUNT_TYPE_OPTIONS}
          value={formData.accountType}
          onChange={value => setFormData(prev => ({ ...prev, accountType: value as AccountType, parentId: '', code: '' }))}
        />
      </FormField>
      <FormField label="Parent Account">
        <Select
          options={parentOptions}
          value={formData.parentId}
          onChange={value => setFormData(prev => ({ ...prev, parentId: value }))}
        />
      </FormField>
    </div>
  )
}

function computeNextSubCode(parentCode: string, siblings: Account[]): string | null {
  // Entity-linked accounts (e.g. 1200-C001) are created via party management, not here
  if (parentCode.includes('-')) {return null}

  const numericSiblings = siblings
    .map(a => a.code)
    .filter((c): c is string => !!c && /^\d+$/.test(c))
    .map(c => parseInt(c, 10))

  if (numericSiblings.length === 0) {
    // No children yet — suggest parentCode + 10 as first child (1500 → 1510)
    return String(parseInt(parentCode, 10) + 10)
  }
  // Suggest max existing + 1 (e.g. siblings 1510, 1520, 1590 → 1591)
  return String(Math.max(...numericSiblings) + 1)
}

function AutoManagedNotice({ selectedParent }: { selectedParent: Account | null | undefined }): JSX.Element {
  return (
    <div className="col-span-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2.5">
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Auto-managed ledger</p>
        <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
          Sub-accounts under <strong>{selectedParent?.code} – {selectedParent?.name}</strong> are created automatically when customers or suppliers are added. Select a different parent to create a regular account.
        </p>
      </div>
    </div>
  )
}

interface SiblingsHintProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  siblings: Account[]
  nextCode: string | null
}

function SiblingsHint({ formData, setFormData, siblings, nextCode }: SiblingsHintProps): JSX.Element {
  const visibleSiblings = siblings.slice(0, 3)
  const hiddenCount = siblings.length - 3
  return (
    <div className="col-span-2 flex items-center gap-1.5 overflow-hidden">
      {nextCode && formData.code !== nextCode && (
        <button
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, code: nextCode }))}
        >
          Next: {nextCode}
        </button>
      )}
      {siblings.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground">Used:</span>
          {visibleSiblings.map(s => (
            <span key={s.id} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-muted text-muted-foreground border border-border whitespace-nowrap">
              {s.code}
            </span>
          ))}
          {hiddenCount > 0 && (
            <SiblingsPopup count={hiddenCount} siblings={siblings} />
          )}
        </>
      )}
    </div>
  )
}

function useAutoFillCodeOnParentChange(args: {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  editingAccount: Account | null
  nextCode: string | null
}): void {
  const { formData, setFormData, editingAccount, nextCode } = args
  const prevParentIdRef = useRef<string>(formData.parentId)
  useEffect(() => {
    if (editingAccount) {return}
    if (formData.parentId === prevParentIdRef.current) {return}
    prevParentIdRef.current = formData.parentId
    setFormData(prev => ({ ...prev, code: nextCode ?? '' }))
  }, [formData.parentId, nextCode, editingAccount, setFormData])
}

function useCodeAndNameRowState(args: {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  editingAccount: Account | null
  accountsMap: Map<string, Account>
}): {
  selectedParent: Account | null
  isEntityControlParent: boolean
  siblings: Account[]
  nextCode: string | null
} {
  const { formData, setFormData, editingAccount, accountsMap } = args
  const selectedParent = formData.parentId ? (accountsMap.get(formData.parentId) ?? null) : null
  const isEntityControlParent = !!(
    selectedParent?.isControl &&
    ENTITY_MANAGED_SUBTYPES.has(selectedParent.accountSubtype ?? '')
  )

  const siblings = useMemo(
    () => Array.from(accountsMap.values()).filter(a => a.parentId === formData.parentId && a.id !== editingAccount?.id),
    [accountsMap, formData.parentId, editingAccount],
  )

  const nextCode = useMemo(
    () => (selectedParent && !isEntityControlParent) ? computeNextSubCode(selectedParent.code, siblings) : null,
    [selectedParent, isEntityControlParent, siblings],
  )

  useAutoFillCodeOnParentChange({ formData, setFormData, editingAccount, nextCode })

  return { selectedParent, isEntityControlParent, siblings, nextCode }
}

function CodeAndNameRow({ formData, setFormData, editingAccount, accountsMap, codeError, codeWarning }: RowProps & { accountsMap: Map<string, Account>; codeError: string | undefined; codeWarning: string | undefined }): JSX.Element {
  const { selectedParent, isEntityControlParent, siblings, nextCode } = useCodeAndNameRowState({
    formData, setFormData, editingAccount, accountsMap,
  })

  if (!editingAccount && isEntityControlParent) {
    return <AutoManagedNotice selectedParent={selectedParent} />
  }

  const prefixHint = selectedParent
    ? undefined
    : `Must start with ${(TYPE_PREFIXES[formData.accountType] ?? []).join(' or ')}`

  return (
    <div className="grid grid-cols-2 el-gap-field">
      <FormField required error={codeError} helperText={!codeError ? prefixHint : undefined} label="Account Code">
        <Input
          placeholder={nextCode ?? 'e.g., 1000'}
          value={formData.code}
          onChange={e => setFormData(prev => ({ ...prev, code: e.target.value }))}
        />
      </FormField>

      <FormField required label="Account Name">
        <Input
          placeholder="e.g., Cash and Cash Equivalents"
          value={formData.name}
          onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </FormField>

      {!editingAccount && selectedParent && (
        <SiblingsHint formData={formData} nextCode={nextCode} setFormData={setFormData} siblings={siblings} />
      )}

      {codeWarning && (
        <div className="col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{codeWarning}. You can still save — this is a system-reserved code and may conflict with automated features.</span>
        </div>
      )}
    </div>
  )
}

interface TagsFieldProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  tagInput: string
  setTagInput: (v: string) => void
}

function TagsField({ formData, setFormData, tagInput, setTagInput }: TagsFieldProps): JSX.Element {
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const value = normalizeTag(tagInput)
      if (value && !formData.tags.includes(value)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, value] }))
      }
      setTagInput('')
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      setFormData(prev => ({ ...prev, tags: prev.tags.slice(0, -1) }))
    }
  }

  return (
    <FormField helperText="Press Enter or comma to add a tag. Used for filtering and quick forms." label="Custom Tags">
      <div className="flex flex-wrap items-center gap-1.5 min-h-[2.25rem] px-3 py-1.5 rounded-md border border-input bg-background">
        {formData.tags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            {tag}
            <button
              className="ml-0.5 hover:text-destructive"
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          placeholder={formData.tags.length === 0 ? 'e.g., vendor_payment, office_supplies' : 'Add tag...'}
          type="text"
          value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
    </FormField>
  )
}

